import React from 'react';
import { useTranslation } from 'react-i18next';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { IconButton, Typography } from '@mui/material';
import { KeyboardArrowRight, KeyboardArrowDown } from '@mui/icons-material';

import { StyledFlex } from 'src/components/StyledFlex';

import { EsgDmaMatrixDatapointSchema } from '../types';

import {
  TableContainer,
  MultiRowHeader,
  HeaderCell,
  GroupRow,
  DataRow,
  Cell,
} from './styles';
import colors from 'src/theme/colors';
import { displayValueOrFallback, getBackgroundColor } from './helpers';
import { useCurrentDma, useEsgSubSubTopic, useEsgSubTopic } from '../hooks';

interface Props {
  data: EsgDmaMatrixDatapointSchema[];
  onToggleDataPointVisibility: (id: number) => void;
  hiddenDataPoints: number[];
}

// Special key for direct items (no subtopic or subsubtopic)
const SPECIAL_KEY = '__SPECIAL__';

// Helper to generate a unique key for sub-groups, handling null esg_sub_topic_id
const getSubTopicKey = (subTopicId: number | null): string => {
  return subTopicId === null ? SPECIAL_KEY : `sub_${subTopicId}`;
};

// Helper to generate a unique key for sub-sub-groups, handling null or virtual esg_sub_sub_topic_id
const getSubSubTopicKey = (
  subSubTopicId: number | null,
  isVirtual: boolean
): string => {
  if (subSubTopicId === null || isVirtual) return SPECIAL_KEY;
  return `subsub_${subSubTopicId}`;
};

function ScoreCell({ score }: { score: number | null }) {
  return (
    <Cell
      style={{
        fontSize: '18px',
        fontWeight: 400,
        backgroundColor: getBackgroundColor(score),
      }}
    >
      {displayValueOrFallback(score)}
    </Cell>
  );
}

export default function DmaMatrixTable(props: Props) {
  const { t } = useTranslation();
  const { data, hiddenDataPoints, onToggleDataPointVisibility } = props;
  const { dma } = useCurrentDma();
  const { subTopicsMap } = useEsgSubTopic({ esg_dma_id: dma?.id });
  const { _dataMap: subSubTopicsMap } = useEsgSubSubTopic({
    esg_dma_id: dma?.id,
  });

  // Helper function to render sub-topic titles
  const renderSubTopicTitle = (subTopicKey: string) => {
    if (subTopicKey === SPECIAL_KEY) {
      return '-';
    }
    // Assuming subTopicKey is like "sub_123"
    const subTopicId = parseInt(subTopicKey.replace('sub_', ''), 10);
    return subTopicsMap[subTopicId]?.description ?? '-';
  };

  // Helper function to render sub-sub-topic titles
  const renderSubSubTopicTitle = (subSubTopicKey: string) => {
    if (subSubTopicKey === SPECIAL_KEY) {
      return '-';
    }
    // Assuming subSubTopicKey is like "subsub_123"
    const subSubTopicId = parseInt(subSubTopicKey.replace('subsub_', ''), 10);
    return subSubTopicsMap[subSubTopicId]?.description ?? '-';
  };

  // Check if a sub-subtopic is virtual
  const isSubSubTopicVirtual = (subSubTopicId: number | null): boolean => {
    if (subSubTopicId === null) return false;
    return !!subSubTopicsMap[subSubTopicId]?.is_virtual;
  };

  // Group data by `esg_topic_key`, then by `esg_sub_topic_id`, and finally by `esg_sub_sub_topic_id`.
  const groupedData = React.useMemo(() => {
    const primaryGroupMap: Record<
      string,
      Record<string, Record<string, EsgDmaMatrixDatapointSchema[]>>
    > = {};

    data.forEach((item) => {
      // Initialize topic level if needed
      if (!primaryGroupMap[item.esg_topic_key]) {
        primaryGroupMap[item.esg_topic_key] = {};
      }

      // Get subtopic key
      const subTopicKey = getSubTopicKey(item.esg_sub_topic_id);
      if (!primaryGroupMap[item.esg_topic_key][subTopicKey]) {
        primaryGroupMap[item.esg_topic_key][subTopicKey] = {};
      }

      // Check if the sub-subtopic is virtual
      const isVirtual = isSubSubTopicVirtual(item.esg_sub_sub_topic_id);
      const subSubTopicKey = getSubSubTopicKey(
        item.esg_sub_sub_topic_id,
        isVirtual
      );

      if (!primaryGroupMap[item.esg_topic_key][subTopicKey][subSubTopicKey]) {
        primaryGroupMap[item.esg_topic_key][subTopicKey][subSubTopicKey] = [];
      }

      primaryGroupMap[item.esg_topic_key][subTopicKey][subSubTopicKey].push(
        item
      );
    });

    return Object.entries(primaryGroupMap)
      .sort(([topicKeyA], [topicKeyB]) => topicKeyA.localeCompare(topicKeyB))
      .map(([topicKey, subGroupsMap]) => ({
        topicKey,
        subGroups: Object.entries(subGroupsMap).map(
          ([subTopicKey, subSubGroupsMap]) => {
            // Get direct/special items (including virtual sub-subtopics)
            const specialItems = subSubGroupsMap[SPECIAL_KEY] || [];

            // Filter out the special key from the subSubGroups
            const regularSubSubGroups = Object.entries(subSubGroupsMap)
              .filter(([key]) => key !== SPECIAL_KEY)
              .map(([subSubTopicKey, items]) => ({
                subSubTopicKey,
                items,
              }));

            return {
              subTopicKey,
              // Regular sub-subtopic groups
              subSubGroups: regularSubSubGroups,
              // Items directly under the subtopic (no sub-subtopic or virtual sub-subtopic)
              directItems: specialItems,
            };
          }
        ),
      }));
  }, [data, subSubTopicsMap]);

  //Track collapsed groups for primary (topic), secondary (sub-topic), and tertiary (sub-sub-topic) levels.
  // Key format: topicKey for primary, topicKey|subTopicKey for secondary, topicKey|subTopicKey|subSubTopicKey for tertiary.
  const [collapsedState, setCollapsedState] = React.useState<
    Record<string, boolean>
  >({});

  const handleToggleCollapse = React.useCallback((key: string) => {
    setCollapsedState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  // Group checkbox for show/hide all items.
  const handleToggleGroupVisibility = React.useCallback(
    (itemsToToggle: EsgDmaMatrixDatapointSchema[]) => {
      const allHidden = itemsToToggle.every((m) =>
        hiddenDataPoints.includes(m.id)
      );
      itemsToToggle.forEach((m) => {
        const isHidden = hiddenDataPoints.includes(m.id);
        if (allHidden && isHidden) {
          // unhide
          onToggleDataPointVisibility(m.id);
        } else if (!allHidden && !isHidden) {
          // hide
          onToggleDataPointVisibility(m.id);
        }
      });
    },
    [hiddenDataPoints, onToggleDataPointVisibility]
  );

  // Render a single matter row
  const renderMatterRow = React.useCallback(
    (matter: EsgDmaMatrixDatapointSchema, indentLevel: number = 90) => (
      <DataRow key={matter.id}>
        {/* Combined Symbol and Matter title cell */}
        <Cell
          style={{
            gridColumn: '1 / span 2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            paddingLeft: `${indentLevel}px`, // Configurable indent for items
          }}
        >
          <StyledFlex
            sx={{
              width: '32px',
              height: '32px',
              backgroundColor: matter.esg_topic_color,
              borderRadius: '50%',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              marginRight: '8px',
              flexShrink: 0,
            }}
          >
            {matter.id}
          </StyledFlex>
          {matter.esg_topic_title}
        </Cell>

        {/*  Score: Wpływ (nonfinancial) */}
        <ScoreCell score={matter.average_score?.nonfinancial ?? null} />

        {/*  Score: Finansowa (financial) */}
        <ScoreCell score={matter.average_score?.financial ?? null} />

        {/*  Impact level */}
        <Cell
          style={{
            backgroundColor: colors.backgroundGrey,
            fontSize: '12px',
          }}
        >
          {t(`esg:dmaGrid.impactLevel.${matter.impact_level}`)}
        </Cell>

        {/*  Show/Hide checkbox */}
        <Cell>
          <IconButton
            type="button"
            onClick={() => handleToggleGroupVisibility([matter])}
            sx={{ color: colors.esgDark }}
          >
            {hiddenDataPoints.includes(matter.id) ? (
              <CheckBoxOutlineBlankIcon />
            ) : (
              <CheckBoxIcon />
            )}
          </IconButton>
        </Cell>
      </DataRow>
    ),
    [hiddenDataPoints, handleToggleGroupVisibility, t]
  );

  return (
    <TableContainer>
      <MultiRowHeader>
        <HeaderCell
          style={{
            gridRow: '1 / 3',
            gridColumn: '1 / 2',
          }}
        >
          {t('esg:dmaGrid.symbol')}
        </HeaderCell>
        <HeaderCell
          style={{
            gridRow: '1 / 3',
            gridColumn: '2 / 3',
          }}
        >
          {t('esg:dmaGrid.matter')}
        </HeaderCell>
        <HeaderCell
          style={{
            gridRow: '1 / 2',
            gridColumn: '3 / 5',
          }}
        >
          {t('esg:dmaGrid.materiality')}
        </HeaderCell>
        <HeaderCell
          style={{
            gridRow: '1 / 3',
            gridColumn: '5 / 6',
          }}
        >
          {t('esg:dmaGrid.impact')}
        </HeaderCell>
        <HeaderCell
          style={{
            gridRow: '1 / 3',
            gridColumn: '6 / 7',
          }}
        >
          {t('esg:dmaGrid.showOnGraph')}
        </HeaderCell>

        <HeaderCell
          style={{
            gridRow: '2 / 3',
            gridColumn: '3 / 4',
          }}
        >
          {t('esg:dmaGrid.impact')}
        </HeaderCell>
        <HeaderCell
          style={{
            gridRow: '2 / 3',
            gridColumn: '4 / 5',
          }}
        >
          {t('esg:dmaGrid.financial')}
        </HeaderCell>
      </MultiRowHeader>

      {groupedData.map(({ topicKey, subGroups }) => {
        const isTopicCollapsed = collapsedState[topicKey] ?? false;
        // Determine if all items within this entire topicKey group are hidden
        const allItemsInTopic = subGroups.flatMap((sg) => [
          ...sg.directItems,
          ...sg.subSubGroups.flatMap((ssg) => ssg.items),
        ]);
        const allItemsInTopicHidden = allItemsInTopic.every((m) =>
          hiddenDataPoints.includes(m.id)
        );

        return (
          <React.Fragment key={topicKey}>
            {/* PRIMARY GROUP ROW (esg_topic_key) */}
            <GroupRow>
              <Cell
                style={{
                  gridColumn: '1 / span 5',
                  justifyContent: 'flex-start',
                }}
              >
                <button
                  type="button"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    margin: '12px 8px 8px 8px',
                  }}
                  onClick={() => handleToggleCollapse(topicKey)}
                >
                  {isTopicCollapsed ? (
                    <KeyboardArrowRight />
                  ) : (
                    <KeyboardArrowDown />
                  )}
                </button>
                <Typography variant="h6">{topicKey}</Typography>
              </Cell>
              <Cell>
                <IconButton
                  type="button"
                  onClick={() => handleToggleGroupVisibility(allItemsInTopic)}
                  sx={{ color: colors.esgDark }}
                >
                  {allItemsInTopicHidden ? (
                    <CheckBoxOutlineBlankIcon />
                  ) : (
                    <CheckBoxIcon />
                  )}
                </IconButton>
              </Cell>
            </GroupRow>

            {!isTopicCollapsed &&
              subGroups.map(({ subTopicKey, subSubGroups, directItems }) => {
                const subGroupGlobalKey = `${topicKey}|${subTopicKey}`;
                const isSubGroupCollapsed =
                  collapsedState[subGroupGlobalKey] ?? false;

                // All items in this subGroup (both direct and in subSubGroups)
                const allItemsInSubGroup = [
                  ...directItems,
                  ...subSubGroups.flatMap((ssg) => ssg.items),
                ];

                // Determine if all items within this specific subGroup are hidden
                const allItemsInSubGroupHidden = allItemsInSubGroup.every((m) =>
                  hiddenDataPoints.includes(m.id)
                );

                // Placeholder for sub-topic display name.
                const subTopicDisplayName = renderSubTopicTitle(subTopicKey);

                return (
                  <React.Fragment key={subGroupGlobalKey}>
                    {/* SUB GROUP ROW (esg_sub_topic_id) */}
                    <GroupRow style={{ backgroundColor: colors.background }}>
                      <Cell
                        style={{
                          gridColumn: '1 / span 5',
                          justifyContent: 'flex-start',
                          paddingLeft: '30px', // Indent sub-group
                        }}
                      >
                        <button
                          type="button"
                          style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            margin: '12px 8px 8px 8px',
                          }}
                          onClick={() =>
                            handleToggleCollapse(subGroupGlobalKey)
                          }
                        >
                          {isSubGroupCollapsed ? (
                            <KeyboardArrowRight />
                          ) : (
                            <KeyboardArrowDown />
                          )}
                        </button>
                        <Typography variant="subtitle1">
                          {subTopicDisplayName}
                        </Typography>
                      </Cell>
                      <Cell>
                        <IconButton
                          type="button"
                          onClick={() =>
                            handleToggleGroupVisibility(allItemsInSubGroup)
                          }
                          sx={{ color: colors.esgDark }}
                        >
                          {allItemsInSubGroupHidden ? (
                            <CheckBoxOutlineBlankIcon />
                          ) : (
                            <CheckBoxIcon />
                          )}
                        </IconButton>
                      </Cell>
                    </GroupRow>

                    {/* If sub-group not collapsed, render direct items first */}
                    {!isSubGroupCollapsed &&
                      directItems.map(
                        (matter) => renderMatterRow(matter, 60) // Use a smaller indent for direct items
                      )}

                    {/* If sub-group not collapsed, render sub-sub-groups */}
                    {!isSubGroupCollapsed &&
                      subSubGroups.map(({ subSubTopicKey, items }) => {
                        const subSubGroupGlobalKey = `${topicKey}|${subTopicKey}|${subSubTopicKey}`;
                        const isSubSubGroupCollapsed =
                          collapsedState[subSubGroupGlobalKey] ?? false;

                        // Determine if all items within this specific subSubGroup are hidden
                        const allItemsInSubSubGroupHidden = items.every((m) =>
                          hiddenDataPoints.includes(m.id)
                        );

                        // Sub-sub-topic display name
                        const subSubTopicDisplayName =
                          renderSubSubTopicTitle(subSubTopicKey);

                        return (
                          <React.Fragment key={subSubGroupGlobalKey}>
                            {/* SUB-SUB GROUP ROW (esg_sub_sub_topic_id) */}
                            <GroupRow
                              style={{ backgroundColor: colors.backgroundGrey }}
                            >
                              <Cell
                                style={{
                                  gridColumn: '1 / span 5',
                                  justifyContent: 'flex-start',
                                  paddingLeft: '60px', // Indent sub-sub-group
                                }}
                              >
                                <button
                                  type="button"
                                  style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    margin: '12px 8px 8px 8px',
                                  }}
                                  onClick={() =>
                                    handleToggleCollapse(subSubGroupGlobalKey)
                                  }
                                >
                                  {isSubSubGroupCollapsed ? (
                                    <KeyboardArrowRight />
                                  ) : (
                                    <KeyboardArrowDown />
                                  )}
                                </button>
                                <Typography variant="subtitle2">
                                  {subSubTopicDisplayName}
                                </Typography>
                              </Cell>
                              <Cell>
                                <IconButton
                                  type="button"
                                  onClick={() =>
                                    handleToggleGroupVisibility(items)
                                  }
                                  sx={{ color: colors.esgDark }}
                                >
                                  {allItemsInSubSubGroupHidden ? (
                                    <CheckBoxOutlineBlankIcon />
                                  ) : (
                                    <CheckBoxIcon />
                                  )}
                                </IconButton>
                              </Cell>
                            </GroupRow>

                            {/* If not collapsed, render each matter in a data row */}
                            {!isSubSubGroupCollapsed &&
                              items.map((matter) =>
                                renderMatterRow(matter, 90)
                              )}
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                );
              })}
          </React.Fragment>
        );
      })}
    </TableContainer>
  );
}
